import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { PageHeader } from "@/components/base/PageHeader";
import { LoadingState } from "@/components/base/LoadingState";
import { SettingsNav } from "@/pages/settings/components/SettingsNav";
import { AdminOnly } from "@/components/feature/AdminOnly";

interface RoleRow {
  id: string;
  key: string;
  name: string;
  description: string | null;
  permissions: unknown;
}

export default function RolesSettings() {
  const { data, isLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await supabase.from("roles").select("id, key, name, description, permissions").order("name");
      return (data ?? []) as RoleRow[];
    },
  });

  return (
    <AdminOnly title="Roles & Permissions">
    <div>
      <PageHeader title="Roles & Permissions" subtitle="Access control roles available to your team" />
      <div className="mb-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-500">
        <i className="ri-information-line text-slate-400 mr-1.5" />
        Role capabilities are enforced by the platform. The owner and administrator roles manage
        people, connectors, and organization settings.
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {isLoading ? (
            <div className="bg-white rounded-xl border border-slate-200 p-8"><LoadingState /></div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50/70 border-b border-slate-100">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Role</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Key</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data ?? []).map((r) => (
                      <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700 capitalize">
                            {r.name}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 font-mono">{r.key}</td>
                        <td className="px-4 py-3 text-sm text-slate-600">{r.description ?? "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        <SettingsNav />
      </div>
    </div>
    </AdminOnly>
  );
}